import styled from "@emotion/styled";
import { Box, systemWidth, useTheme } from "@mediaspace/shared/styled";
import { Button, Typography } from "@kaltura/ds-react-components";
import { useMediaQuery } from "@mediaspace/shared/utils";
import { useButtonAnalytics } from "@mediaspace/hooks";
import { ButtonClickAnalyticsType } from "@mediaspace/shared/types/ButtonClickAnalyticsType";

export interface BannerCtaProps {
    headerText?: string;
    descriptionText?: string;
    bannerImage?: string;
    buttonText?: string;
    buttonUrl?: string;
}

export const StyledBanner = styled(Box)(({ theme }) => ({
    "&": systemWidth({ theme }),
    minHeight: theme.typography.pxToRem(247),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    justifyContent: 'center',
    borderRadius: theme.kaltura.shape.roundness2,
    padding: theme.spacing(7, 3),
    [theme.breakpoints.down("sm")]: {
        padding: theme.spacing(5, 3),
    },
}));

export const StyledTitle = styled(Typography)(({ theme }) => ({
    marginTop: 0,
}));


export const StyledCtaButton = styled(Button)(({ theme }) => ({
    marginTop: theme.spacing(3),
    minWidth: theme.typography.pxToRem(200)
}));

/**
 * Banner CTA component - banner with background image, title, description and CTA button - all are optional
 */
export function BannerCta(props: BannerCtaProps) {
    const {
        headerText = '',
        descriptionText = '',
        bannerImage = '',
        buttonText = '',
        buttonUrl = ''
    } = props;
    const theme = useTheme();
    const isXsScreen = useMediaQuery(theme.breakpoints.down(theme.breakpoints.values.sm));
    const sendButtonAnalytics = useButtonAnalytics();
    return (
        <StyledBanner
            className={"kms-ds-homepage-component-banner-cta-container"}
            style={{
                    background: bannerImage ?
                        `linear-gradient(180deg, rgba(0, 0, 0, 0.00) 0%, rgba(0, 0, 0, 0.60) 100%), url(${bannerImage}) lightgray 50% / cover no-repeat`
                        : theme.kaltura.palette.tone7,
                    color: bannerImage ? theme.palette.common.white : theme.kaltura.palette.tone1
            }}
        >
            {headerText &&
                <StyledTitle
                    className={"kms-ds-homepage-component-banner-cta-title"}
                    variant={isXsScreen ? "h3" : "h2"}
                >
                    {headerText}
                </StyledTitle>
            }
            {descriptionText &&
                <Typography
                    className={"kms-ds-homepage-component-banner-cta-description"}
                    variant={"body1"}
                >
                    {descriptionText}
                </Typography>
            }
            {buttonText && buttonUrl &&
                <StyledCtaButton
                    size={"large"}
                    className={"kms-ds-homepage-component-banner-cta-button"}
                    href={buttonUrl}
                    onClick={() => sendButtonAnalytics(
                        'Custom page - Banner CTA link',
                        ButtonClickAnalyticsType.LINK
                    )}
                >
                    {buttonText}
                </StyledCtaButton>
            }
        </StyledBanner>
    );
}

export default BannerCta;
